import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import Moment from "moment";
import {
  createStyles,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Theme,
  WithStyles,
  withStyles,
  Switch,
  Button,
  Checkbox,
  Tooltip,
} from "@material-ui/core";
import { GlobalState } from "../../types/globalState";
import { Game, GState } from "../../types/game";
import gameActions from "../../actions/games";
import Loading from "../Loading";
import ChainIcon from "./ChainIcon";
import { AxiosError } from "axios";
import { hideAuxiliaryDialog } from "../SharedComponents/AuxiliaryDialog";

interface Props extends WithStyles<typeof styles> {
  // Function to link or unlink games.
  linkGames: (gameIds: Array<string>, mainScoringGame: string, callback?: (err: AxiosError | null) => void) => void;
  unlinkGame: (gameId: string, callback?: (err: AxiosError | null) => void) => void;
  // Function to fetch games the user has joined.
  getJoinedGames: (callback?: (err: AxiosError | null, data: Array<Game>) => void) => void;
  // Function to fetch games the user has organised.
  getCurrentOrganisedGames: (callback?: (err: AxiosError | null, data: Array<Game>) => void) => void;
  // The current game context.
  game: Game;
  // A list of all games (joined and organised) that are linkable with the current game.
  games: Array<Game>;
}

// LinkedGamesList component allows users to link multiple games together,
// typically for scoring purposes across a series of games.
const LinkedGamesList: React.FC<Props> = ({
  classes,
  game,
  games,
  linkGames,
  unlinkGame,
  getCurrentOrganisedGames,
  getJoinedGames,
}) => {
  const [searchText, setSearchText] = useState("");
  // State for the list of game IDs that are currently selected for linking.
  const [selectedGames, setSelectedGames] = useState<Array<string>>([]);
  // State for the ID of the game designated as the main scoring game among the linked ones.
  const [mainScoringGame, setMainScoringGame] = useState<string | null>(game._id);

  // useMemo to compute the set of already linked games and the main scoring game from the props.
  // This recalculates only when the `games` prop changes.
  const linkedGames = useMemo(() => {
    const result = games.reduce<{ linkedGames: Set<string>; mainScoringGame: string | null }>(
      (acc, curr) => {
        // Accumulate all unique IDs of games that are linked to any game in the list.
        curr.linkedGames?.forEach(id => acc.linkedGames.add(id));
        // If no main scoring game is set yet and the current game has one, set it.
        if (!acc.mainScoringGame && curr.mainScoringGame) acc.mainScoringGame = curr.mainScoringGame;
        return acc;
      },
      {
        linkedGames: new Set(), // Start with an empty set of linked games.
        mainScoringGame: null, // Start with no main scoring game.
      },
    );

    return {
      linkedGames: Array.from(result.linkedGames), // Convert the set to an array.
      mainScoringGame: result.mainScoringGame,
    };
  }, [games]);

  // useEffect to initialize or update the selected games and main scoring game
  // when the component mounts or when the already linked games change.
  useEffect(() => {
    setSelectedGames(linkedGames.linkedGames);
    setMainScoringGame(linkedGames.mainScoringGame || game._id); // Default to current game if no main scoring game is set.
  }, [linkedGames.linkedGames, linkedGames.mainScoringGame, game._id]);

  // useMemo to filter the games to be displayed based on the search text.
  // This recalculates when `games` or `searchText` changes.
  const gamesToDisplay = useMemo(
    () =>
      games.filter(
        g =>
          g.name.toLowerCase().includes(searchText.toLowerCase()) || // Match by game name.
          g.code.toLowerCase().includes(searchText.toLowerCase()), // Or match by game code.
      ),
    [games, searchText],
  );

  // useCallback for handling the selection and deselection of games.
  // It updates the `selectedGames` list and adjusts `mainScoringGame` if necessary.
  const onSelectAndDeselectGames = useCallback(
    (gameId: string) => {
      setSelectedGames(prev => {
        const isSelected = prev.includes(gameId);
        // Add or remove the gameId from the list.
        const updated = isSelected ? prev.filter(id => id !== gameId) : [...prev, gameId];

        // If the deselected game was the main scoring game,
        // set the first game in the updated list (or the current game) as the new main.
        if (isSelected && mainScoringGame === gameId) {
          setMainScoringGame(updated[0] || game._id);
        } else if (!isSelected && prev.length === 0) {
          // If selecting the first game in an empty list, make it the main scoring game.
          setMainScoringGame(gameId);
        }

        return updated;
      });
    },
    [mainScoringGame, game._id], // Dependencies for the callback.
  );

  // useCallback for setting a game as the main scoring game.
  const handleSetMainScoringGame = useCallback((gameId: string) => {
    setMainScoringGame(gameId);
  }, []);

  // useMemo to determine if the game linking is editable.
  // Linking is editable if no player has started playing (no scores recorded for hole 1).
  const editable = useMemo(() => {
    return !Object.values(game.players || {}).some(
      player => (player.uholes && player.uholes[1]) || (player.holes && player.holes[1]),
    );
  }, [game.players]); // Recalculates if game.players changes.

  // Handler for the save button.
  // It finalizes the list of games to be linked and calls the `linkGames` action.
  const handleSave = () => {
    const finalGameIds = !editable
      ? Array.from(new Set([...(selectedGames.length > 0 ? selectedGames : linkedGames.linkedGames), game._id]))
      : Array.from(new Set([...selectedGames, game._id]));
    const idsForApi = !editable ? finalGameIds : selectedGames;
    const validMainScoring = finalGameIds.includes(mainScoringGame || "") ? mainScoringGame : game._id;

    if (validMainScoring) {
      linkGames(idsForApi, validMainScoring, err => {
        if (!err) {
          getCurrentOrganisedGames?.();
          getJoinedGames?.();
        }
      });
    }
    hideAuxiliaryDialog();
  };

  const savedMain = game.mainScoringGame || game._id;
  const mainChanged = mainScoringGame !== savedMain;

  return (
    <div style={{ minWidth: 400, display: "flex", flexDirection: "column", height: "100%" }}>
      <Loading style={{ position: "sticky", top: 50, zIndex: 101 }} />

      <div className={classes.header}>
        <TextField
          fullWidth
          variant="outlined"
          label="Filter by Name or Game Code"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>

      {/* List of games to display, allowing selection and setting the main scoring game. */}
      <List style={{ flexGrow: 1, overflow: "auto" }}>
        {gamesToDisplay.map((g, index) => (
          <div key={g._id} style={{ width: "100%", position: "relative" }}>
            <ListItem
              button
              onClick={() => {
                if (editable) onSelectAndDeselectGames(g._id);
              }}
              dense
            >
              <Tooltip title="Select to include this game in the link group. Unselect to remove.">
                <Checkbox
                  checked={selectedGames.includes(g._id)}
                  disabled={!editable}
                  onChange={() => onSelectAndDeselectGames(g._id)}
                  onClick={e => e.stopPropagation()}
                  color="primary"
                />
              </Tooltip>
              {/* Display game name and code. */}
              <ListItemText primary={g.name} secondary={g.code} />
              {/* Display game date and time, formatted. */}
              <ListItemText
                secondary={Moment(g.date).format("ddd DD/MM/YY HH:mm")}
                style={{ textAlign: "right", paddingRight: "50px" }}
              />
              {/* Switch to set a game as the main scoring game. */}
              <ListItemSecondaryAction>
                <Tooltip
                  title={
                    selectedGames.includes(g._id)
                      ? "Toggle to set this game as the main scoring game for the linked group."
                      : "Select the game (checkbox) to enable setting it as main scoring game."
                  }
                >
                  <Switch
                    disabled={!selectedGames.includes(g._id)} // Disabled if the game is not selected.
                    checked={mainScoringGame === g._id} // Checked if this game is the main scoring game.
                    onChange={() => handleSetMainScoringGame(g._id)}
                    onClick={e => e.stopPropagation()} // Prevent ListItem's onClick from firing.
                    color="primary"
                  />
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            {/* Icon to visually indicate game linking status or order. */}
            <ChainIcon currentGame={g} nextGame={gamesToDisplay[index + 1]} />
          </div>
        ))}

        {gamesToDisplay.length === 0 && (
          <ListItem>
            <ListItemText
              secondary={
                games.length === 0 ? "There are no linkable games matching criteria." : "No games match your filter."
              }
            />
          </ListItem>
        )}
      </List>

      <div className={classes.footer}>
        {linkedGames.linkedGames.length > 0 ? (
          <Button
            color="secondary"
            variant="outlined"
            className={classes.button}
            onClick={() => {
              if (window.confirm("Unlink this game from its linked games?")) {
                unlinkGame(game._id, err => {
                  if (!err) {
                    getCurrentOrganisedGames?.();
                    getJoinedGames?.();
                    hideAuxiliaryDialog();
                  }
                });
              }
            }}
          >
            Unlink
          </Button>
        ) : null}
        <Button color="secondary" variant="contained" className={classes.button} onClick={hideAuxiliaryDialog}>
          Cancel
        </Button>
        {editable || mainChanged ? (
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={
              editable
                ? selectedGames.length === 0 && linkedGames.linkedGames.length === 0
                : !(mainChanged && (linkedGames.linkedGames.length > 0 || selectedGames.length > 0))
            }
            onClick={handleSave}
          >
            Save
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    footer: {
      position: "sticky",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: 100,
      display: "flex",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    button: {
      flex: 1,
      borderRadius: 0,
      padding: theme.spacing(1.5, 2),
      textTransform: "none",
    },
    header: {
      padding: theme.spacing(1, 2),
      margin: theme.spacing(0.5),
      zIndex: 100,
      position: "sticky",
      top: 0,
      backgroundColor: theme.palette.background.paper,
    },
  });

// Helper function to sort games.
// It prioritizes games already linked to the current game, then sorts by date, then by name.
const sortGames = (games: Array<Game>, current: Game): Array<Game> => {
  return [...games].sort((a, b) => {
    const aLinked = current.linkedGames?.includes(a._id);
    const bLinked = current.linkedGames?.includes(b._id);
    if (aLinked && !bLinked) return -1; // a comes first if linked and b is not.
    if (!aLinked && bLinked) return 1; // b comes first if linked and a is not.
    // Otherwise, sort by date, then by name as a tie-breaker.
    return Moment(a.date).valueOf() - Moment(b.date).valueOf() || a.name.localeCompare(b.name);
  });
};

// Helper function to filter games that are linkable with the current game.
// Linkable games are those on the same course, on the same day, and not yet completed.
const filterLinkableGames = (allGames: Array<Game>, current: Game): Array<Game> => {
  const currentDate = Moment(current.date);
  return allGames.filter(g => {
    const sameCourse = g.course?._id === current.course?._id;
    const sameDay = currentDate.isSame(Moment(g.date), "day");
    const notDone = g.state !== GState.Done;
    return sameCourse && sameDay && notDone;
  });
};

// mapStateToProps function for Redux.
// It filters and sorts all games (organised and joined) to get the list of linkable games for the current game.
const mapStateToProps = (state: GlobalState, ownProps: { game: Game }) => {
  const allGames = Object.values({ ...state.games.organisedByMe, ...state.games.joinedGames });
  const filtered = filterLinkableGames(allGames, ownProps.game);
  return {
    games: sortGames(filtered, ownProps.game), // Provide the sorted, linkable games as a prop.
  };
};

export default connect(mapStateToProps, {
  linkGames: gameActions.linkGames,
  unlinkGame: gameActions.unlinkGame,
  getJoinedGames: gameActions.getJoinedGames,
  getCurrentOrganisedGames: gameActions.getCurrentOrganisedGames,
})(withStyles(styles)(LinkedGamesList));
